import React, { useRef, useEffect, ComponentType } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExcelExport, ExcelExportColumn, ExcelExportColumnGroup } from '@progress/kendo-react-excel-export';
import { GridCellProps, GridCellsSettings, GridColumnType } from '@progress/kendo-react-grid';
import moment from 'moment';
import { ScoreColor, ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import { useTranslation } from 'hooks';
import { convertBooleanToYesNo, convertColorEnumToString, convertDirectionEnumToString } from 'utils/convertor';
import { isSplittedValue, validOverviewDate } from '@utils/validation';

export const getReportDateFormat = (dateString: string) => {
    const dateResult = isSplittedValue(dateString) ? dateString.split('|')[0] : dateString;
    const dateFormatResult = isSplittedValue(dateString) ? dateString.split('|')[1] : '';

    return dateResult ? moment(dateResult).format(dateFormatResult) : '';
};

export interface ExportExcelComponentInputColumn {
    field?: string;
    title?: string;
    label?: string;
    minWidth?: number;
    show?: boolean;
    filter?: string;
    cell?: ComponentType<GridCellProps>;
    children?: this[];
    multiValueExportTitles?: { [key: string]: string };
    orderIndex?: number;
    cellName?: string;
    cells?: GridCellsSettings;
    lovData?: string[] | undefined;
    filterKey?: string;
    columnType?: GridColumnType | undefined;
    alwaysExportColumn?: boolean;
}

interface ExportExcelComponentProps {
    columns: ExportExcelComponentInputColumn[];
    mapColumns: ((columns: ExportExcelComponentInputColumn[]) => ExportExcelComponentInputColumn[]) | null;
    data: object[];
    fileName: string;
    exportTrigger?: number; // use this property to trigger export from the parent component
    onExportComplete: () => void;
}

const ExportExcelComponent = ({
    columns,
    mapColumns,
    data,
    fileName,
    exportTrigger,
    onExportComplete,
}: ExportExcelComponentProps) => {
    const exportRef = useRef<ExcelExport | null>(null);

    const { t } = useTranslation();

    const exportToExcel = () => {
        exportRef.current?.save();
    };

    const exportColumns = mapColumns ? mapColumns(columns) : columns;

    useEffect(() => {
        if (exportTrigger) {
            exportToExcel();
        }
    }, [exportTrigger]);

    const isDateColumn = (column: ExportExcelComponentInputColumn) => column.cellName === 'DateCell';

    const isDateOrDateTimeColumn = (column: ExportExcelComponentInputColumn) => column.filter === 'date';

    const getColumnByFieldName = (fieldName: string) =>
        exportColumns.find((column: ExportExcelComponentInputColumn) => column.field === fieldName);

    const getChildColumnByFieldName = (fieldName: string) => {
        let foundColumn: ExportExcelComponentInputColumn | undefined;
        exportColumns.forEach((col) => {
            if (col.children && !foundColumn) {
                foundColumn = col.children.find(
                    (column: ExportExcelComponentInputColumn) => column.field === fieldName,
                );
            }
        });
        return foundColumn;
    };

    const convertFieldString = (value: string, fieldName: string) => {
        let column = getColumnByFieldName(fieldName);
        if (!column) column = getChildColumnByFieldName(fieldName);

        if (isSplittedValue(value) && validOverviewDate(value)) return value ? getReportDateFormat(value) : value; // Study/Site Overview date display

        if (!column || !isDateOrDateTimeColumn(column)) return value;

        const date = moment.utc(value);

        if (!date.isValid()) return value;

        return isDateColumn(column) ? date.format('DD-MMM-YYYY') : date.format('DD-MMM-YYYY HH:mm:ss');
    };

    const convertFieldValues = (object: { [key: string]: unknown }, objectName?: string) => {
        Object.keys(object).forEach((key: string) => {
            const fieldName = objectName ? `${objectName}.${key}` : key;

            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            object[key] = convertFieldValue(object[key], fieldName);
        });
    };

    const escapeDoubleQuotes = (value: string) => value.replace(/"/g, '""');
    const wrapInDoubleQuotes = (value: string) => `"${value}"`;

    const applyFormulaInjectionSanitization = (value: string): string => {
        return wrapInDoubleQuotes(`'${escapeDoubleQuotes(value)}`);
    };

    const escapeFormulaInjection = (value: string): string => {
        // Starts with any number (including zero) of tab (\t), carriage return (\r), or newline (\n) characters.
        // The next character is one of: =, +, -, or @.
        const needsFormulaEscape = /^\s*[=+\-@]/.test(value);
        return needsFormulaEscape ? applyFormulaInjectionSanitization(value) : value;
    };

    const convertFieldValue = (value: unknown, fieldName: string) => {
        if (value === null || value === undefined) return '';

        if (typeof value === 'object') convertFieldValues(value as { [key: string]: unknown }, fieldName);

        if (fieldName.includes('.changeDirection')) return convertDirectionEnumToString(value as ScoreDirection);

        if (
            fieldName.includes('.colour') ||
            fieldName.includes('.colourInFocus') ||
            fieldName.includes('.comparisonColour')
        ) {
            if (typeof value === 'string') return value;
            return convertColorEnumToString(value as ScoreColor);
        }

        switch (typeof value) {
            case 'boolean':
                return convertBooleanToYesNo(value);
            case 'string':
                return escapeFormulaInjection(convertFieldString(value, fieldName));
            default:
                return value;
        }
    };

    function convertDataRow(row: object): unknown {
        const convertedRow: { [key: string]: unknown } = { ...row };

        convertFieldValues(convertedRow);

        return convertedRow;
    }

    const getData = () => data.map((row: object) => convertDataRow(row));

    const getTitle = (column: ExportExcelComponentInputColumn) => {
        return column.title ?? column.label ?? column.field;
    };

    const excelExportColumns = (expColumns: ExportExcelComponentInputColumn[]) =>
        (expColumns ?? []).map((column: ExportExcelComponentInputColumn) => {
            if (column.columnType === 'checkbox') return undefined;
            if (column.children?.length) {
                return (
                    <ExcelExportColumnGroup title={getTitle(column)} key={`${column.field}.${column.orderIndex}`}>
                        {excelExportColumns(
                            column.children.sort((a, b) =>
                                a.orderIndex === undefined
                                    ? 1
                                    : b.orderIndex === undefined
                                      ? -1
                                      : a.orderIndex - b.orderIndex,
                            ),
                        )}
                    </ExcelExportColumnGroup>
                );
            }

            if (column.multiValueExportTitles) {
                return (
                    <ExcelExportColumnGroup title={getTitle(column)} key={`${column.field}.${column.orderIndex}`}>
                        {Object.keys(column.multiValueExportTitles).map((value) => {
                            const title = column.multiValueExportTitles![value];
                            return (
                                <ExcelExportColumn
                                    key={`${column.field}.${value}.${column.orderIndex}`}
                                    field={`${column.field}.${value}`}
                                    title={title}
                                    width={title.length * 8}
                                    headerCellOptions={{
                                        background: '#fff',
                                        color: '#000',
                                    }}
                                    hidden={column.show === false && !column.alwaysExportColumn}
                                    // If is falsey, i.e., null or undefined, then column should still show by default
                                />
                            );
                        })}
                    </ExcelExportColumnGroup>
                );
            }
            let fieldKey = column.field;
            if (column.filterKey) fieldKey = `${column.field}.${column.filterKey}`;
            return (
                <ExcelExportColumn
                    key={`${column.field}.${column.orderIndex}`}
                    field={fieldKey}
                    title={getTitle(column) || ' '}
                    width={column.minWidth || 180}
                    headerCellOptions={{
                        background: '#fff',
                        color: '#000',
                    }}
                    hidden={column.show === false && !column.alwaysExportColumn}
                    // If column.show is falsey, i.e., null or undefined, then column should still show by default
                />
            );
        });

    return (
        <>
            <button
                type="button"
                className="excel-saver"
                onClick={exportToExcel}
                onKeyDown={exportToExcel}
                data-testid="export-to-excel"
            >
                <FontAwesomeIcon className="icon" icon={['fas', 'file-excel']} />
                <span className="excel-wrapper">
                    <p>{t('exportExcel')}</p>
                </span>
            </button>
            <ExcelExport
                ref={exportRef}
                data={getData()}
                fileName={`${fileName.replace(/[/\\?%*:|"<>.]/g, ' ')}.xlsx` ?? ''}
                onExportComplete={() => onExportComplete()}
            >
                {excelExportColumns(exportColumns)}
            </ExcelExport>
        </>
    );
};

export default ExportExcelComponent;
